// src/pages/LandingPage.jsx — أستاذ محمد العاطفي — Clean Minimalist Design
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import * as d1Api from '../services/d1Api';
import {
    CheckCircle,
    Video,
    Headphones,
    BookOpen,
    Target,
    Clock,
    LogIn,
    UserPlus,
    ArrowLeft,
    X,
    Award,
    Youtube,
    Facebook,
    MessageCircle,
    Bell,
    ShoppingCart,
    User,
    Trophy,
    Instagram
} from 'lucide-react';
import { Footer } from '../components/common/Footer';
import { FloatingWhatsApp } from '../components/common/FloatingWhatsApp';

// Custom TikTok icon
const TikTokIcon = ({ size = 24, className = '' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.76a4.85 4.85 0 01-1.01-.07z"/>
    </svg>
);

// WhatsApp icon
const WhatsAppIcon = ({ size = 24, className = '' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
);

export const LandingPage = () => {
    const [courses, setCourses] = useState([]);
    const [topStudents, setTopStudents] = useState([]);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [loading, setLoading] = useState(true);
    const [selectedGrade, setSelectedGrade] = useState(null);
    const { user } = useAuth();

    useEffect(() => {
        const handleScroll = () => {
            const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
            const currentProgress = (window.scrollY / totalScroll) * 100;
            setScrollProgress(currentProgress);
        };
        window.addEventListener('scroll', handleScroll);

        // Fetch live courses from D1 API
        const fetchCourses = async () => {
            try {
                const res = await d1Api.getAllCourses();
                if (res.success && res.data && res.data.length > 0) {
                    setCourses(res.data);
                } else {
                    setCourses([]);
                }
            } catch (err) {
                console.error('Error fetching courses:', err);
                setCourses([]);
            } finally {
                setLoading(false);
            }
        };
        fetchCourses();

        // Fetch leaderboard
        const fetchLeaderboard = async () => {
            try {
                const res = await d1Api.getLeaderboard();
                if (res.success && res.data && res.data.length > 0) {
                    setTopStudents(res.data.map(s => ({
                        id: s.id,
                        name: [s.firstName, s.middleName, s.lastName].filter(Boolean).join(' '),
                        points: s.points || 0,
                        rank: s.grade === '1sec' ? 'أولى ثانوي' : s.grade === '2sec' ? 'تانية ثانوي' : 'تالتة ثانوي'
                    })));
                } else {
                    setTopStudents([]);
                }
            } catch {
                setTopStudents([]);
            }
        };
        fetchLeaderboard();

        const timer = setTimeout(() => setShowHelpMessage(true), 10000);
        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearTimeout(timer);
        };
    }, []);

    const features = [
        { icon: CheckCircle, title: 'مرونة في المذاكرة', eng: 'Study Anytime, Anywhere', desc: 'ذاكر في أي وقت ومن أي مكان يناسبك' },
        { icon: Video, title: 'فيديوهات بشرح مبسط', eng: 'Crystal-Clear Lessons', desc: 'شرح وافي ومبسط لكل جزئيات المنهج' },
        { icon: Target, title: 'اختبارات مستمرة', eng: 'Regular Assessments', desc: 'تقييم دوري لمستواك مع تصحيح فوري' },
        { icon: Headphones, title: 'دعم فني ومجتمع طلابي', eng: 'Community & Support', desc: 'تواصل مع زملائك واستفسر في أي وقت' },
        { icon: BookOpen, title: 'مصادر متعددة', eng: 'Multiple Resources', desc: 'حل من كل المصادر مش من مصدر واحد' },
    ];

    const grades = [
        { id: '1sec', label: 'المرحلة الثانوية', tag: 'إنجليزي أولى ثانوي' },
        { id: '2sec', label: 'المرحلة الثانوية', tag: 'إنجليزي تانية ثانوي' },
        { id: '3sec', label: 'إنجليزي تالتة ثانوي', tag: 'للصف الثالث الثانوي' },
    ];

    const filteredCourses = selectedGrade
        ? courses.filter(c => c.grade === selectedGrade && c.status !== 'hidden')
        : courses.filter(c => c.status !== 'hidden');

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F8F9FC 0%, #EEF0F5 50%, #F0ECE3 100%)' }}>
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-[#C9A84C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-[#1A1A2E] font-bold text-lg">جاري التحميل...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen relative overflow-x-hidden" dir="rtl" style={{ background: 'linear-gradient(135deg, #F8F9FC 0%, #EEF0F5 50%, #F0ECE3 100%)' }}>

            {/* ── FLOATING DECORATIVE ELEMENTS ── */}
            {/* Glow orbs */}
            <div className="glow-orb" style={{ top: '10%', right: '5%', width: '300px', height: '300px' }}></div>
            <div className="glow-orb" style={{ top: '50%', left: '3%', width: '250px', height: '250px', animationDelay: '3s' }}></div>
            <div className="glow-orb" style={{ bottom: '20%', right: '10%', width: '200px', height: '200px', animationDelay: '5s' }}></div>
            {/* Gold particles */}
            <div className="gold-particle" style={{ top: '15%', right: '20%' }}></div>
            <div className="gold-particle" style={{ top: '35%', left: '15%' }}></div>
            <div className="gold-particle" style={{ top: '60%', right: '30%' }}></div>
            <div className="gold-particle" style={{ top: '80%', left: '25%' }}></div>
            <div className="gold-particle" style={{ top: '45%', right: '8%' }}></div>

            {/* ── SCROLL PROGRESS BAR ── */}
            <div className="fixed top-0 right-0 left-0 h-1 z-[999]">
                <div
                    className="h-full transition-all duration-150"
                    style={{ width: `${scrollProgress}%`, background: 'linear-gradient(90deg, #1A1A2E, #C9A84C)' }}
                />
            </div>

            {/* ══════════════════════════════════════
                TOP BANNER — Animated English Quote
            ══════════════════════════════════════ */}
            <div className="w-full py-2 flex items-center justify-center gap-4 overflow-hidden" style={{ background: 'linear-gradient(90deg, #1A1A2E, #2D2D44, #1A1A2E)' }}>
                <div className="flex items-center gap-2">
                    <span className="text-[#C9A84C] text-xs">✦</span>
                    <p className="text-white/80 font-medium text-xs md:text-sm tracking-wider" style={{ fontFamily: "'Georgia', serif", fontStyle: 'italic' }}>
                        "The limits of my language mean the limits of my world." — Wittgenstein
                    </p>
                    <span className="text-[#C9A84C] text-xs">✦</span>
                </div>
            </div>

            {/* ══════════════════════════════════════
                STICKY NAVBAR — Premium Glass
            ══════════════════════════════════════ */}
            <header className="sticky top-0 z-50 px-3 py-2 md:px-6 md:py-3" style={{ background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', borderBottom: '1px solid rgba(201,168,76,0.15)', boxShadow: '0 4px 30px rgba(0,0,0,0.05)' }}>
                <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between">
                    
                    {/* RIGHT: Logo */}
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg" style={{ background: 'linear-gradient(135deg, #C9A84C, #A68A3E)' }}>
                            <span className="text-white font-black text-lg" style={{ fontFamily: "'Georgia', serif" }}>M</span>
                        </div>
                        <div className="flex flex-col leading-tight">
                            <span className="text-sm md:text-base text-[#1A1A2E] font-black" style={{ fontFamily: "'Georgia', serif" }}>Mr. Al-Atfy</span>
                            <span className="text-[9px] md:text-[10px] text-[#C9A84C] font-bold tracking-[0.2em] uppercase">English Language</span>
                        </div>
                    </div>

                    {/* LEFT: Actions */}
                    <div className="flex items-center gap-2 order-2 md:order-3">
                        {user ? (
                            <button
                                onClick={() => window.location.href = user?.role === 'admin' ? '/admin' : '/student'}
                                className="text-white px-5 py-2 rounded-full font-bold transition flex items-center gap-2 shadow-lg text-sm hover:shadow-xl hover:scale-105"
                                style={{ background: 'linear-gradient(135deg, #1A1A2E, #2D2D44)' }}
                            >
                                <ArrowLeft size={15} />
                                حسابي
                            </button>
                        ) : (
                            <>
                                <button
                                    onClick={() => setShowLoginModal(true)}
                                    className="text-[#1A1A2E] hover:text-[#C9A84C] font-bold text-sm transition hidden md:flex items-center gap-1"
                                >
                                    تسجيل الدخول
                                </button>
                                <button
                                    onClick={() => window.location.href = '/login?tab=register'}
                                    className="text-white font-bold px-5 py-2.5 rounded-full text-sm flex items-center gap-2 shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                                    style={{ background: 'linear-gradient(135deg, #C9A84C, #A68A3E)' }}
                                >
                                    <UserPlus size={14} />
                                    سجّل الآن
                                </button>
                            </>
                        )}
                    </div>

                    {/* CENTER: Nav links */}
                    <nav className="flex w-full md:w-auto order-3 md:order-2 overflow-x-auto hide-scrollbar gap-6 text-[12px] md:text-sm font-semibold text-gray-500 mt-2 pt-2 border-t border-gray-100 md:mt-0 md:pt-0 md:border-t-0 justify-center whitespace-nowrap">
                        <a href="#" className="hover:text-[#C9A84C] transition-colors">Home</a>
                        <a href="#courses" className="hover:text-[#C9A84C] transition-colors">Courses</a>
                        <a href="#features" className="hover:text-[#C9A84C] transition-colors">Features</a>
                        <a href="#honor" className="hover:text-[#C9A84C] transition-colors">Hall of Fame</a>
                    </nav>
                </div>
            </header>

            {/* ══════════════════════════════════════
                HERO — Premium Split Layout
            ══════════════════════════════════════ */}
            <section className="relative z-10 overflow-hidden" style={{ background: 'linear-gradient(160deg, #0F0F1A 0%, #1A1A2E 40%, #2D2D44 100%)' }}>
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-10" style={{ background: 'radial-gradient(circle, rgba(201,168,76,0.4), transparent 70%)' }}></div>
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-5" style={{ background: 'radial-gradient(circle, rgba(201,168,76,0.5), transparent 70%)' }}></div>
                {/* Grid pattern overlay */}
                <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(201,168,76,1) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,1) 1px, transparent 1px)', backgroundSize: '60px 60px' }}></div>

                <div className="container mx-auto px-4 py-16 md:py-20">
                    <div className="flex flex-col md:flex-row items-center gap-10 md:gap-16">
                        
                        {/* LEFT SIDE — Text Content */}
                        <div className="flex-1 text-center md:text-right order-2 md:order-1 relative z-10">
                            {/* Badge */}
                            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6 text-xs font-bold" style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', color: '#C9A84C' }}>
                                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                                Now Enrolling — Secondary Stage
                            </div>

                            {/* Main heading */}
                            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black leading-[1.1] mb-4" style={{ fontFamily: "'Georgia', serif" }}>
                                <span className="text-white">Master</span>{' '}
                                <span className="text-[#C9A84C]">English</span>
                                <br />
                                <span className="text-white/60 text-2xl md:text-3xl lg:text-4xl font-bold">with</span>{' '}
                                <span className="text-white">Mr. Al-Atfy</span>
                            </h1>

                            {/* Arabic name */}
                            <p className="text-xl md:text-2xl font-black cairo text-white/40 mb-6">
                                أ. محمد العاطفي
                            </p>

                            {/* Description */}
                            <p className="text-white/50 text-sm md:text-base max-w-md mx-auto md:mr-0 md:ml-auto leading-relaxed mb-8" style={{ fontFamily: "'Georgia', serif" }}>
                                Elevate your English skills with expert guidance, comprehensive video lessons, and interactive assessments designed for secondary students.
                            </p>

                            {/* CTA Buttons */}
                            <div className="flex flex-col sm:flex-row items-center justify-center md:justify-end gap-3 mb-10">
                                <button
                                    onClick={() => window.location.href = '/login?tab=register'}
                                    className="group inline-flex items-center gap-3 text-[#1A1A2E] font-black text-base px-8 py-4 rounded-full shadow-2xl transition-all hover:scale-105 hover:shadow-[0_0_40px_rgba(201,168,76,0.4)]"
                                    style={{ background: 'linear-gradient(135deg, #C9A84C, #DFC06A)' }}
                                >
                                    <UserPlus size={20} />
                                    ابدأ رحلتك الآن
                                    <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
                                </button>
                                <button
                                    onClick={() => setShowLoginModal(true)}
                                    className="inline-flex items-center gap-2 text-white/70 hover:text-white font-semibold text-sm px-6 py-3.5 rounded-full transition-all hover:bg-white/5"
                                    style={{ border: '1px solid rgba(255,255,255,0.15)' }}
                                >
                                    <LogIn size={16} />
                                    تسجيل الدخول
                                </button>
                            </div>

                        </div>

                        {/* RIGHT SIDE — Photo */}
                        <div className="flex-shrink-0 order-1 md:order-2 relative">
                            {/* Glow behind photo */}
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] md:w-[450px] md:h-[450px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(201,168,76,0.2) 0%, transparent 70%)', filter: 'blur(30px)' }}></div>
                            
                            {/* Photo container */}
                            <div className="relative w-[260px] h-[340px] md:w-[320px] md:h-[420px]">
                                {/* Gold border frame */}
                                <div className="absolute inset-0 rounded-3xl" style={{ border: '2px solid rgba(201,168,76,0.25)', transform: 'rotate(3deg)' }}></div>
                                <div className="absolute inset-0 rounded-3xl overflow-hidden shadow-2xl" style={{ border: '2px solid rgba(201,168,76,0.15)' }}>
                                    <img
                                        src="/images/doctor-hero.png"
                                        alt="Mr. Mohamed Al-Atfy — English Language Teacher"
                                        className="w-full h-full object-cover object-top"
                                        onError={(e) => { e.target.src = '/images/image_bb8b98.jpg'; }}
                                    />
                                    {/* Gradient overlay at bottom */}
                                    <div className="absolute bottom-0 left-0 right-0 h-24" style={{ background: 'linear-gradient(to top, rgba(15,15,26,0.9), transparent)' }}></div>
                                    {/* Name badge at bottom of photo */}
                                    <div className="absolute bottom-4 left-4 right-4 text-center">
                                        <p className="text-white font-black text-sm" style={{ fontFamily: "'Georgia', serif" }}>Mr. Mohamed Al-Atfy</p>
                                        <p className="text-[#C9A84C] text-[10px] tracking-[0.2em] uppercase font-bold">English Teacher</p>
                                    </div>
                                </div>
                                {/* Floating accent badge */}
                                <div className="absolute -bottom-3 -right-3 md:-bottom-4 md:-right-4 px-4 py-2 rounded-xl shadow-xl" style={{ background: 'linear-gradient(135deg, #C9A84C, #A68A3E)' }}>
                                    <span className="text-white font-black text-xs">🎓 Secondary</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom wave */}
                <svg viewBox="0 0 1440 80" fill="none" className="w-full -mb-1" preserveAspectRatio="none" style={{ height: '60px' }}>
                    <path d="M0 80 C360 30 720 70 1080 40 C1260 25 1380 50 1440 45 L1440 80 L0 80 Z" fill="#FAFAF8" />
                </svg>
            </section>


            {/* ══════════════════════════════════════
                GRADE SELECTION — Dark bg
            ══════════════════════════════════════ */}
            <section className="relative z-10 py-16" style={{ background: 'linear-gradient(160deg, #0F0F1A 0%, #1A1A2E 40%, #2D2D44 100%)' }}>
                {/* Grid pattern */}
                <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(201,168,76,1) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,1) 1px, transparent 1px)', backgroundSize: '50px 50px' }}></div>
                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center mb-12">
                        <p className="text-sm font-bold tracking-[0.2em] uppercase mb-2" style={{ color: '#C9A84C', fontFamily: "'Georgia', serif" }}>
                            Choose Your Grade
                        </p>
                        <h2 className="text-3xl md:text-4xl font-black text-white mb-3 cairo">
                            اختر <span style={{ color: '#C9A84C' }}>صفك الدراسي</span>
                        </h2>
                        <p className="text-white/50 max-w-xl mx-auto text-sm" style={{ fontFamily: "'Georgia', serif" }}>
                            Select your grade level to explore available courses
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
                        {grades.map((grade) => (
                            <div
                                key={grade.id}
                                onClick={() => setSelectedGrade(selectedGrade === grade.id ? null : grade.id)}
                                className="rounded-2xl p-6 text-center cursor-pointer transition-all duration-300 hover:-translate-y-2"
                                style={{
                                    background: selectedGrade === grade.id
                                        ? 'linear-gradient(135deg, #C9A84C, #DFC06A)'
                                        : 'rgba(255,255,255,0.08)',
                                    backdropFilter: 'blur(16px)',
                                    WebkitBackdropFilter: 'blur(16px)',
                                    border: `2px solid ${selectedGrade === grade.id ? '#C9A84C' : 'rgba(201,168,76,0.2)'}`,
                                    boxShadow: selectedGrade === grade.id ? '0 10px 40px rgba(201,168,76,0.3)' : '0 4px 20px rgba(0,0,0,0.1)'
                                }}
                            >
                                <div className="px-4 py-2 rounded-xl flex items-center justify-center mx-auto mb-4 w-max min-w-[120px] min-h-[64px]"
                                    style={{
                                        background: selectedGrade === grade.id ? '#1A1A2E' : '#C9A84C',
                                        boxShadow: '0 8px 20px rgba(201,168,76,0.3)'
                                    }}>
                                    <span className="text-base md:text-lg font-black"
                                        style={{ color: selectedGrade === grade.id ? '#C9A84C' : '#1A1A2E' }}>
                                        {grade.tag}
                                    </span>
                                </div>
                                <h3 className="text-xl font-black cairo"
                                    style={{ color: selectedGrade === grade.id ? '#1A1A2E' : '#FFFFFF' }}>
                                    {grade.label}
                                </h3>
                                {selectedGrade === grade.id && (
                                    <div className="mt-2 inline-block px-3 py-0.5 rounded-full text-xs font-black"
                                        style={{ background: '#1A1A2E', color: '#C9A84C' }}>
                                        ✓ محدد
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Wave up back to white */}
            <div className="relative -mt-1 z-10" style={{ lineHeight: 0, background: '#1A1A2E' }}>
                <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg"
                    className="w-full" preserveAspectRatio="none" style={{ height: '60px' }}>
                    <path d="M0 80 C360 0 720 60 1080 20 C1260 0 1380 40 1440 30 L1440 80 L0 80 Z"
                        fill="#FAFAF8" />
                </svg>
            </div>

            {/* ══════════════════════════════════════
                FEATURES SECTION
            ══════════════════════════════════════ */}
            <section id="features" className="ornate-section relative z-10 py-16">
                {/* Shimmer sweep */}
                <div className="shimmer-line"></div>
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <p className="text-sm font-bold tracking-[0.2em] uppercase mb-2" style={{ color: '#C9A84C', fontFamily: "'Georgia', serif" }}>
                            Why Choose Us
                        </p>
                        <h2 className="text-3xl md:text-4xl font-black text-[#1A1A2E] mb-3 cairo">
                            ليه تختار <span style={{ color: '#C9A84C' }}>منصتنا</span>؟
                        </h2>
                        <p className="text-gray-500 max-w-xl mx-auto cairo">
                            منصتنا مش مجرد شرح، دي تجربة متكاملة عشان الطالب يحقق أعلى الدرجات
                        </p>
                        {/* Ornate divider */}
                        <div className="ornate-divider"><div className="ornate-divider-dot"></div></div>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                        {features.map((feature, idx) => (
                            <div key={idx} className="glass-card p-6 group cursor-pointer" style={{ background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-all group-hover:rotate-3 group-hover:scale-110"
                                    style={{ background: 'linear-gradient(135deg, rgba(201,168,76,0.15), rgba(201,168,76,0.05))', border: '1px solid rgba(201,168,76,0.2)' }}>
                                    <feature.icon size={26} style={{ color: '#C9A84C' }} />
                                </div>
                                <h3 className="text-lg font-black text-[#1A1A2E] mb-1 cairo">{feature.title}</h3>
                                <p className="text-xs font-bold tracking-wider mb-2" style={{ color: '#C9A84C', fontFamily: "'Georgia', serif" }}>{feature.eng}</p>
                                <p className="text-sm text-gray-500 leading-relaxed cairo">{feature.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ══════════════════════════════════════
                COURSES SECTION
            ══════════════════════════════════════ */}
            <section id="courses" className="ornate-section relative z-10 py-16">
                <div className="shimmer-line" style={{ animationDelay: '2s' }}></div>
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <p className="text-sm font-bold tracking-[0.2em] uppercase mb-2" style={{ color: '#C9A84C', fontFamily: "'Georgia', serif" }}>
                            Available Courses
                        </p>
                        <h2 className="text-3xl md:text-4xl font-black text-[#1A1A2E] mb-3 cairo">
                            الكورسات <span style={{ color: '#C9A84C' }}>المتاحة</span>
                        </h2>
                        <p className="text-gray-500 max-w-xl mx-auto cairo">
                            اختر الكورس المناسب لمستواك وابدأ رحلة التميز
                        </p>
                        <div className="ornate-divider"><div className="ornate-divider-dot"></div></div>
                    </div>

                    {filteredCourses.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
                                style={{ background: 'rgba(26,26,46,0.08)' }}>
                                <BookOpen size={36} style={{ color: '#1A1A2E' }} />
                            </div>
                            <p className="text-gray-400 font-bold cairo text-lg">لا توجد كورسات متاحة حالياً</p>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                            {filteredCourses.map((course) => {
                                const isLocked = course.status === 'locked';
                                return (
                                    <div
                                        key={course.id}
                                        className={`course-card ${isLocked ? 'opacity-75 cursor-not-allowed' : 'cursor-pointer'}`}
                                        onClick={() => {
                                            if (isLocked) return;
                                            if (!user) setShowLoginModal(true);
                                        }}
                                    >
                                        <div className="h-48 relative overflow-hidden"
                                            style={{ background: 'linear-gradient(135deg, #1A1A2E, #2D2D44)' }}>
                                            <img
                                                src={course.image_url || '/images/doctor-hero.png'}
                                                alt={course.title}
                                                className="w-full h-full object-cover opacity-80 transition duration-500 hover:opacity-95"
                                                onError={(e) => { e.target.src = '/images/image_bb8b98.jpg'; }}
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                                            {isLocked && (
                                                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                                    <div className="bg-white/90 px-4 py-2 rounded-xl font-bold text-sm text-[#1A1A2E] cairo">
                                                        🔒 الحجز مغلق
                                                    </div>
                                                </div>
                                            )}
                                            <span className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-black cairo"
                                                style={{ background: '#C9A84C', color: '#1A1A2E' }}>
                                                {course.grade === '1sec' ? 'أولى ثانوي' : course.grade === '2sec' ? 'تانية ثانوي' : 'تالتة ثانوي'}
                                            </span>
                                            <div className="absolute bottom-3 left-3 px-3 py-1.5 rounded-xl font-black text-sm shadow-lg"
                                                style={{ background: '#FFFFFF', color: '#1A1A2E' }}>
                                                {course.price} <span className="text-xs">ج.م</span>
                                            </div>
                                        </div>
                                        <div className="p-5">
                                            <h3 className="text-lg font-black mb-2 text-[#1A1A2E] cairo">{course.title}</h3>
                                            <p className="text-sm mb-4 line-clamp-2 text-gray-500 cairo">{course.description}</p>
                                            <div className="flex items-center justify-between border-t border-gray-100 pt-4">
                                                <span className="text-sm flex items-center gap-1 text-gray-400 cairo">
                                                    <Clock size={14} />
                                                    {course.duration_days || course.duration || 30} يوم
                                                </span>
                                                {isLocked ? (
                                                    <span className="text-gray-400 font-bold text-sm cairo">🔒 مغلق</span>
                                                ) : (
                                                    <button className="bg-[#28A745] hover:bg-[#1e8449] text-white px-4 py-1.5 rounded-full font-black text-sm transition-all hover:scale-105 cairo">
                                                        اشترك الآن
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>

            {/* ══════════════════════════════════════
                HONOR BOARD (LEADERBOARD)
            ══════════════════════════════════════ */}
            <section id="honor" className="ornate-section relative z-10 py-16">
                <div className="shimmer-line" style={{ animationDelay: '1s' }}></div>
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <p className="text-sm font-bold tracking-[0.2em] uppercase mb-2" style={{ color: '#C9A84C', fontFamily: "'Georgia', serif" }}>
                            Hall of Fame
                        </p>
                        <h2 className="text-3xl md:text-4xl font-black text-[#1A1A2E] mb-3 cairo">
                            🏆 لوحة <span style={{ color: '#C9A84C' }}>الشرف</span>
                        </h2>
                        <p className="text-gray-500 max-w-xl mx-auto cairo">
                            نخبة المتميزين في رحلتنا نحو التفوق
                        </p>
                        <div className="ornate-divider"><div className="ornate-divider-dot"></div></div>
                    </div>

                    {topStudents.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
                                style={{ background: 'rgba(26,26,46,0.08)' }}>
                                <Trophy size={40} style={{ color: '#1A1A2E' }} />
                            </div>
                            <p className="text-gray-400 font-bold cairo text-lg">
                                قريباً... سيتم تحديد لوحة الشرف من قبل الإدارة
                            </p>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                            {topStudents.map((student, i) => (
                                <div key={student.id}
                                    className="p-6 text-center rounded-2xl hover:-translate-y-2 transition-all shadow-lg relative overflow-hidden" style={{ background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(201,168,76,0.12)' }}>
                                    <div className="absolute top-0 left-0 w-full h-1"
                                        style={{ background: 'linear-gradient(90deg, #1A1A2E, #C9A84C)' }}></div>
                                    <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center border-4"
                                        style={{ background: 'rgba(26,26,46,0.08)', borderColor: '#C9A84C' }}>
                                        <span className="text-3xl">{i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'}</span>
                                    </div>
                                    <h3 className="text-xl font-black text-[#1A1A2E] mb-2 cairo">{student.name}</h3>
                                    <div className="inline-block px-3 py-1 rounded-full text-sm font-bold mb-3 cairo"
                                        style={{ background: 'rgba(26,26,46,0.08)', color: '#1A1A2E' }}>
                                        {student.rank}
                                    </div>
                                    <div className="text-2xl font-black cairo" style={{ color: '#C9A84C' }}>{student.points}</div>
                                    <div className="text-sm text-gray-400 cairo">نقطة</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Replace old footer with the new Footer component */}
            <Footer />

            {/* Login Modal */}
            {showLoginModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
                    onClick={() => setShowLoginModal(false)}>
                    <div className="p-8 max-w-md w-full animate-in rounded-2xl shadow-2xl relative overflow-hidden"
                        style={{ background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', border: '1px solid rgba(201,168,76,0.15)' }}
                        onClick={e => e.stopPropagation()}>
                        
                        <div className="absolute top-0 left-0 w-full h-1"
                            style={{ background: 'linear-gradient(90deg, #1A1A2E, #C9A84C)' }}></div>

                        <button
                            onClick={() => setShowLoginModal(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
                        >
                            <X size={24} />
                        </button>

                        <div className="text-center mb-6 mt-2">
                            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
                                style={{ background: '#1A1A2E' }}>
                                <img src="/images/logo.png" alt="Logo" className="w-10 h-10 object-contain" onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<span style="font-weight:900;color:#C9A84C;font-size:1.5rem">أ</span>'; }} />
                            </div>
                            <h2 className="text-2xl font-black text-[#1A1A2E] mb-1">
                                أهلاً بك في منصة أستاذ محمد العاطفي
                            </h2>
                            <p className="text-gray-500 cairo text-sm">سجل دخولك وابدأ رحلة التعلم</p>
                        </div>

                        <div className="space-y-4">
                            <button
                                onClick={() => { setShowLoginModal(false); window.location.href = '/login'; }}
                                className="w-full text-white p-4 rounded-xl font-black flex items-center justify-center gap-3 transition hover:scale-[1.02] cairo shadow-lg"
                                style={{ background: '#1A1A2E' }}
                            >
                                <LogIn size={20} />
                                الانتقال لتسجيل الدخول
                            </button>

                            <div className="flex items-center gap-3 py-1">
                                <div className="flex-1 h-px bg-gray-200"></div>
                                <span className="text-xs text-gray-400 font-bold cairo">أو</span>
                                <div className="flex-1 h-px bg-gray-200"></div>
                            </div>

                            <button
                                onClick={() => { setShowLoginModal(false); window.location.href = '/login?tab=register'; }}
                                className="w-full border-2 border-[#1A1A2E] text-[#1A1A2E] hover:bg-[#1A1A2E] hover:text-white p-4 rounded-xl font-black flex items-center justify-center gap-3 transition-all hover:scale-[1.02] cairo"
                            >
                                <UserPlus size={20} />
                                إنشاء حساب جديد
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Floating WhatsApp Bubble */}
            <FloatingWhatsApp />
        </div>
    );
};
